import { Box, Button, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import PortalClosedBanner from '@/components/application_components/PortalClosedBanner';
import QuestionSwitch from '@/components/application_components/question_types/QuestionSwitch';
import { Spinner } from '@/components/common/Spinner';
import DeadlineBanner from '@/components/dashboard_components/applicant_dashboard/DeadlineBanner';
import GrowthHeader from '@/components/growth_award_components/GrowthHeader';
import Header from '@/components/header/Header';
import {
  CurrentApplicationContext,
  CurrentApplicationProvider,
} from '@/Contexts/CurrentApplicationContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import useApplication from '@/hooks/useApplication';
import useQuestions from '@/hooks/useQuestions';
import { ApplicationTypes } from '@/models/ApplicationType';
import { QuestionProps } from '@/models/Question';
import { ApplicationStatus } from '@/utils/constants';
import { logError } from '@/utils/errorHandlerUtil';

function StateSpecificContent() {
  const [stateQuestions, setStateQuestions] = useState<QuestionProps[] | null>(null);
  const { answers, canEdit, currentApplication, currentContacts, partnerInfo } =
    useContext(CurrentApplicationContext);
  const { locale } = useContext(LocaleContext);

  const theme = useTheme();
  const navigate = useNavigate();
  const { postSubmit } = useApplication();

  const { portalOpen } = useContext(SelectedProgramContext);

  const { updateApplicationStatus } = useContext(DashboardContext);

  const { getQuestions } = useQuestions();
  const [headerName, setHeaderName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [submitButtonText, setSubmitButtonText] = useState<string>('Submit State Questions');
  const [submitClicked, setSubmitClicked] = useState<boolean>(false);

  useEffect(() => {
    if (
      loading &&
      stateQuestions != null &&
      stateQuestions.length > 0 &&
      currentApplication.id != null &&
      partnerInfo != null &&
      answers != null &&
      currentContacts != null
    ) {
      setLoading(false);
    }
  }, [
    currentApplication.id,
    getQuestions,
    loading,
    currentContacts,
    partnerInfo,
    answers,
    stateQuestions,
  ]);

  useEffect(() => {
    if (partnerInfo != null) {
      setHeaderName(partnerInfo.partnerName ?? '');
    }
  }, [partnerInfo]);

  useEffect(() => {
    if (
      currentApplication.applicationTemplateId != null &&
      stateQuestions == null &&
      locale != null
    ) {
      const fetchQuestions = async () => {
        const questions = await getQuestions(currentApplication.applicationTemplateId, locale);
        if (questions == null) {
          navigate('/error');
        } else {
          setStateQuestions(questions.standards[0].questions);
        }
      };

      void fetchQuestions();
    }
  }, [currentApplication.applicationTemplateId, getQuestions, locale, navigate, stateQuestions]);

  return (
    <Box display='flex' flexDirection='column' height='100vh' width='100vw'>
      <DeadlineBanner />
      <Header />
      {loading ? (
        <Spinner />
      ) : (
        <Box
          sx={{
            overflowX: 'hidden',
            scrollbarGutter: 'stable',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <GrowthHeader name={headerName} />
          <Box display='flex' flex={1} justifyContent='center' className='wrap'>
            <Box
              display='flex'
              flexDirection='column'
              flexGrow={1}
              gap='10px'
              minWidth='500px'
              maxWidth='1080px'
              alignItems='left'
            >
              <Box display='flex' alignItems='center' justifyContent='center'>
                <Typography variant='h2'>State Specific Questions</Typography>
              </Box>

              <Box
                display='flex'
                flexDirection='column'
                padding={5}
                sx={{ backgroundColor: theme.palette.sandShade1.main }}
                gap={1}
              >
                {partnerInfo != null &&
                  stateQuestions
                    ?.sort(
                      (a: QuestionProps, b: QuestionProps) => a.questionOrder - b.questionOrder
                    )
                    .map((q: QuestionProps) => {
                      return (
                        <Box key={`${q.id}-box`}>
                          <QuestionSwitch key={q.id} question={q} />
                        </Box>
                      );
                    })}

                {portalOpen == null ||
                  (portalOpen[ApplicationTypes.StateQuestions] === false && !canEdit && (
                    <PortalClosedBanner applicationTypeId={ApplicationTypes.StateQuestions} />
                  ))}

                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  sx={{ marginTop: '16px' }}
                >
                  <Button
                    disabled={
                      submitClicked ||
                      ((portalOpen == null ||
                        portalOpen[ApplicationTypes.StateQuestions] === false) &&
                        !canEdit)
                    }
                    variant='primary'
                    size='large'
                    onClick={() => {
                      if (currentApplication.id != null) submitState(currentApplication.id);
                    }}
                  >
                    {submitButtonText}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  async function submitState(id: number) {
    setSubmitButtonText('Submitting...');
    setSubmitClicked(true);

    try {
      const updatedApplicationStatus: ApplicationStatus = await postSubmit(id, null);
      setSubmitButtonText('Questions Submitted');
      updateApplicationStatus(updatedApplicationStatus, id);
      setTimeout(() => {
        navigate('/submit-confirmation', {
          state: {
            applicationId: currentApplication.id,
          },
        });
      }, 1000);
    } catch (error) {
      logError(`Error submitting state specific questions for application ${id}.`, error);
      setSubmitButtonText('Submit State Questions');
    } finally {
      setSubmitClicked(false);
    }
  }
}

export default function StateSpecific() {
  const { appid } = useParams();

  return (
    <CurrentApplicationProvider key={appid} appid={appid}>
      <StateSpecificContent />
    </CurrentApplicationProvider>
  );
}
